import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import {
  TOOLTIP_ALIGN_CENTER,
  TOOLTIP_ALIGN_END,
  TOOLTIP_ALIGN_START,
  TOOLTIP_PLACEMENT_BOTTOM,
  TOOLTIP_PLACEMENT_LEFT,
  TOOLTIP_PLACEMENT_RIGHT,
  TOOLTIP_PLACEMENT_TOP,
  TOOLTIP_SIZE_SMALL,
  TOOLTIP_VIEWPORT_MARGIN_PX,
  type TooltipAlign,
  type TooltipPlacement,
  type TooltipSize,
} from './Tooltip.constants';
import { Tip, TriggerSpan, Wrapper } from './Tooltip.styled';

export interface TooltipProps {
  /** The trigger element. Tooltip wires up hover/focus/Escape on the wrapper. */
  children: ReactNode;
  /** The tooltip body. Pass a string or any react node. */
  content: ReactNode;
  placement?: TooltipPlacement;
  /**
   * Cross-axis alignment against the trigger: `start` lines the tip's
   * leading edge up with the trigger's, so a tip on a trigger near the
   * container edge grows inward instead of being clipped. Defaults to `center`.
   */
  align?: TooltipAlign;
  /** `medium` is the roomier tip for page descriptions and nav-rail labels. */
  size?: TooltipSize;
  /**
   * Force the tooltip open regardless of hover/focus. Useful for stories,
   * demos, or "show on selection" patterns. Leave undefined for normal
   * hover/focus behaviour.
   */
  open?: boolean;
  /** Called whenever the open state changes from internal events. */
  onOpenChange?: (open: boolean) => void;
  /**
   * When true, render the tooltip body via a React portal (default
   * target: `document.body`) using `position: fixed`. Use this for
   * triggers that live inside an `overflow: hidden` ancestor — the
   * portaled tip escapes that clipping box and renders above any
   * stacking context above the trigger.
   *
   * Position is computed from the trigger's bounding rect on open and
   * tracks scroll + resize while the tip is visible.
   */
  portal?: boolean;
  /**
   * Optional explicit portal target. Defaults to `document.body` when
   * `portal` is true. Useful for shadow DOM, iframes, or test setups.
   * Ignored when `portal` is false.
   */
  portalContainer?: Element | null;
  /**
   * When true, the wrapper is rendered as a block element that fills
   * its parent's width and the inner trigger span stretches with it.
   * Use when the child owns its own `width: 100%` (e.g. a full-width
   * dropdown row button) and the default `inline-flex` wrapper would
   * otherwise collapse it to content width.
   */
  fullWidth?: boolean;
  className?: string;
  dataTestId?: string;
}

const SPACING_PX = 6;

interface PortalCoords {
  top: number;
  left: number;
}

const alignedOffset = (start: number, size: number, align: TooltipAlign): number => {
  switch (align) {
    case TOOLTIP_ALIGN_START:
      return start;
    case TOOLTIP_ALIGN_CENTER:
      return start + size / 2;
    case TOOLTIP_ALIGN_END:
      return start + size;
    default: {
      const exhaustive: never = align;
      throw new Error(`Unknown tooltip align: ${String(exhaustive)}`);
    }
  }
};

/** How far to slide the tip sideways so it stays inside the viewport, measured without the current slide. */
const viewportNudge = (rect: DOMRect, current: number): number => {
  const left = rect.left - current;
  const right = rect.right - current;
  const maxRight = window.innerWidth - TOOLTIP_VIEWPORT_MARGIN_PX;
  if (right > maxRight) return Math.max(maxRight - right, TOOLTIP_VIEWPORT_MARGIN_PX - left);
  if (left < TOOLTIP_VIEWPORT_MARGIN_PX) return TOOLTIP_VIEWPORT_MARGIN_PX - left;
  return 0;
};

/**
 * Project the trigger's viewport rect onto the portaled tip's anchor point.
 * The styled component pulls the tip's own edge onto that point via
 * transforms, so the tip's width/height stay opaque to JS.
 */
const computePortalCoords = (
  rect: DOMRect,
  placement: TooltipPlacement,
  align: TooltipAlign,
): PortalCoords => {
  const x = alignedOffset(rect.left, rect.width, align);
  const y = alignedOffset(rect.top, rect.height, align);
  switch (placement) {
    case TOOLTIP_PLACEMENT_TOP:
      return { top: rect.top - SPACING_PX, left: x };
    case TOOLTIP_PLACEMENT_BOTTOM:
      return { top: rect.bottom + SPACING_PX, left: x };
    case TOOLTIP_PLACEMENT_LEFT:
      return { top: y, left: rect.left - SPACING_PX };
    case TOOLTIP_PLACEMENT_RIGHT:
      return { top: y, left: rect.right + SPACING_PX };
    default: {
      const exhaustive: never = placement;
      throw new Error(`Unknown tooltip placement: ${String(exhaustive)}`);
    }
  }
};

const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  placement = TOOLTIP_PLACEMENT_BOTTOM,
  align = TOOLTIP_ALIGN_CENTER,
  size = TOOLTIP_SIZE_SMALL,
  open,
  onOpenChange,
  portal = false,
  portalContainer,
  fullWidth = false,
  className,
  dataTestId = 'tooltip',
}) => {
  const isControlled = open !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = isControlled ? !!open : internalOpen;
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLSpanElement>(null);
  const tipId = useId();

  const [coords, setCoords] = useState<PortalCoords | null>(null);
  const [nudge, setNudge] = useState(0);

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onDocClick = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDocClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDocClick);
    };
  }, [isOpen, setOpen]);

  /**
   * In portal mode the tip renders into the portal target via
   * `createPortal`, escaping the trigger's `overflow: hidden`
   * ancestor. Positioning becomes our responsibility — compute
   * coordinates from the trigger's bounding rect on open, and
   * keep them current while the user scrolls or resizes. We
   * listen with `capture: true` so we catch scrolls on every
   * intermediate ancestor (overflow containers between the
   * trigger and the viewport), not just window-level scrolls.
   */
  useLayoutEffect(() => {
    // Closed, the tip keeps its last place: it fades out there instead of at its static position.
    if (!portal || !isOpen) return undefined;
    const updateCoords = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (rect) setCoords(computePortalCoords(rect, placement, align));
    };
    updateCoords();
    window.addEventListener('scroll', updateCoords, true);
    window.addEventListener('resize', updateCoords);
    return () => {
      window.removeEventListener('scroll', updateCoords, true);
      window.removeEventListener('resize', updateCoords);
    };
  }, [portal, isOpen, placement, align]);

  useLayoutEffect(() => {
    const rect = coords ? tipRef.current?.getBoundingClientRect() : undefined;
    setNudge((current) => (rect ? viewportNudge(rect, current) : 0));
  }, [coords]);

  const tip = (
    <Tip
      ref={tipRef}
      id={tipId}
      role="tooltip"
      $placement={placement}
      $align={align}
      $size={size}
      $isOpen={isOpen}
      $portal={portal}
      style={
        portal && coords ? { top: coords.top, left: coords.left + nudge } : undefined
      }
      aria-hidden={!isOpen}
      data-testid={`${dataTestId}__tip`}
    >
      {content}
    </Tip>
  );

  const portalTarget =
    portal && typeof document !== 'undefined'
      ? portalContainer ?? document.body
      : null;

  return (
    <Wrapper
      ref={wrapperRef}
      className={className}
      $fullWidth={fullWidth}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      data-testid={dataTestId}
    >
      <TriggerSpan
        ref={triggerRef}
        $fullWidth={fullWidth}
        aria-describedby={isOpen ? tipId : undefined}
        data-testid={`${dataTestId}__trigger`}
      >
        {children}
      </TriggerSpan>
      {portalTarget ? createPortal(tip, portalTarget) : tip}
    </Wrapper>
  );
};

export default Tooltip;
